import React, { useState, useEffect, useRef } from "react";
import "../assets/css/DevicePolicy.css";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { deletePolicy, getAllDevicePolicies, updateDevicePolicy } from "../commonApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import "bootstrap/dist/css/bootstrap.min.css";
import { Loader } from "../Loader";
import ActionCell from "../Utils/ActionCell";
import moment from "moment";
import { UpdateDevicePolicy } from "./UpdateDevicePolicy";
import { AddDevicePolicy } from './AddDevicePolicy';

function DevicePolicy() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [countPerPage, setCountPerPage] = useState(10);
  const [sortOrder, setSortOrder] = useState("desc");
  const [sortColumn, setSortColumn] = useState("createdOn");
  const [searchTerm, setSearchTerm] = useState("");
  const [devicePolicyData, setDevicePolicyData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePolicyId, setDeletePolicyId] = useState(null);
  const [deleteEle, setDeleteEle] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isDeleteDisabled, setIsDeleteDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showUpdateDeviceModal, setShowUpdateDeviceModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const expectedPageRef = useRef(0); // Added for pagination stability

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        if (!isCancelled) {
          await fetchDevicePolicies();
        }
      } catch (error) {
        if (!isCancelled) {
          toast.error("An error occurred while fetching device policies!");
          console.error("Error fetching device policies:", error);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isCancelled = true;
    };
  }, [page, countPerPage, sortColumn, sortOrder, searchTerm]);

  const fetchDevicePolicies = async () => {
    setLoading(true);
    try {
      const params = {
        pageNo: page,
        pageSize: countPerPage,
        sortBy: sortColumn,
        sortOrder: sortOrder,
        searchValue: searchTerm,
        pagination: true,
      };
      console.log("API Request:", { pageNo: page, pageSize: countPerPage, sortBy: sortColumn, sortOrder, searchValue: searchTerm });
      const response = await getAllDevicePolicies(params);
      const policies = (response?.data?.data?.policies || []).filter(
        (policy) => policy && typeof policy === "object" && policy.name
      );
      console.log("API Response:", {
        page,
        contentLength: policies.length,
        totalElements: response?.data?.data?.pagination?.totalElements,
        totalPages: response?.data?.data?.pagination?.totalPages,
      });
      setDevicePolicyData(policies);
      setTotalRows(response?.data?.data?.pagination?.totalElements || 0);
      if (response?.data?.data?.pagination?.totalElements > 0 && policies.length === 0) {
        toast.warn(`No device policies found for page ${page + 1}`);
      }
    } catch (error) {
      toast.error("An error occurred while fetching device policies!");
      console.error("Error fetching device policies:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInputChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
    expectedPageRef.current = 0;
  };

  const handlePerRowsChange = (newPerPage, newPage) => {
    console.log("Changing rows per page:", { newPerPage, currentPage: page });
    setCountPerPage(newPerPage);
    setPage(0); // Reset to first page
    expectedPageRef.current = 0;
  };

  const handleSort = (column, sortDirection) => {
    setSortColumn(column.sortField);
    setSortOrder(sortDirection);
    setPage(0);
    expectedPageRef.current = 0;
  };

  const handleEdit = (id) => {
    const policyToEdit = devicePolicyData.find((item) => item.id === id);
    setSelectedPolicy(policyToEdit);
    setShowUpdateDeviceModal(true);
  };

  const confirmEdit = async (policyData) => {
    try {
      const { name, description, policyJson } = policyData;
      const response = await updateDevicePolicy(policyData.id, { name, description, policyJson });
      if (response.status === 200) {
        toast.success("Policy Updated Successfully!");
      }
      setShowUpdateDeviceModal(false);
      setSelectedPolicy(null);
      setPage(0); // Reset to first page
      expectedPageRef.current = 0;
      fetchDevicePolicies();
    } catch (error) {
      toast.error(error?.response?.data?.error?.message || "Failed to update the policy!");
    }
  };

  const handleDelete = (id) => {
    setDeletePolicyId(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (isDeleteDisabled) return;
    setIsDeleteDisabled(true);
    try {
      await deletePolicy(deletePolicyId);
      toast.success('Policy deleted successfully');
      setPage(0);
      expectedPageRef.current = 0;
      fetchDevicePolicies();
    } catch (error) {
      if (error.response && error.response.status === 400) {
        toast.error(error.response.data?.error?.message || "Policy is attached, Cannot be Deleted");
      } else {
        toast.error("Failed to delete Policy!");
      }
    } finally {
      setIsDeleteDisabled(false);
      setDeleteModalOpen(false);
      setDeleteEle(null);
    }
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateDeviceModal(false);
    setSelectedPolicy(null);
  };

  const handleCloseAddModal = () => {
    setShowAddDeviceModal(false);
  };

  const handleAddSuccess = () => {
    setShowAddDeviceModal(false);
    setPage(0); // Reset to first page
    expectedPageRef.current = 0;
    fetchDevicePolicies();
  };

  const renderActionCell = (row) => {
    return <ActionCell row={row} onEdit={handleEdit} onDelete={handleDelete} />;
  };

  const columns = [
    {
      name: "Name",
      selector: (row) => row?.name || "",
      sortable: true,
      sortField: "name",
      width: "200px",
    },
    {
      name: "Description",
      selector: (row) => row?.description || "",
      sortable: true,
      sortField: "description",
      width: "300px",
      cell: (row) => (
        <div
          data-bs-toggle="tooltip"
          data-bs-placement="top"
          title={row?.description || ""}
        >
          {row?.description && row.description.length > 20
            ? `${row.description.substring(0, 20)}...`
            : row?.description || ""}
        </div>
      ),
    },
    {
      name: "Version",
      selector: (row) => row?.version || "",
      sortable: true,
      sortField: "version",
      width: "150px",
    },
    {
      name: "Created Date",
      selector: (row) => row?.createdOn || "",
      sortable: true,
      sortField: "createdOn",
      width: "300px",
      cell: (row) => (
        <div style={{ width: "100%" }}>
          {row?.createdOn ? (
            moment(row.createdOn).format("YYYY-MM-DD HH:mm:ss")
          ) : (
            <span style={{ marginLeft: "20%" }}>-</span>
          )}
        </div>
      ),
    },
    {
      name: "Actions",
      cell: renderActionCell,
      right: true,
    },
  ];

  return (
    <div className="custom_tab_wrapper">
      <style>
        {`
          .btn_red {
            background-color: #dc3545;
            border-color: #dc3545;
            color: white;
          }
          .btn_red:disabled {
            background-color: #f28b94;
            border-color: #f28b94;
            color: white;
            opacity: 0.8;
          }
          .btn_light__red {
            background-color: #f8d7da;
            border-color: #f5c6cb;
            color: #721c24;
          }
          .btn_light__red:disabled {
            background-color: #f8d7da;
            border-color: #f5c6cb;
            color: #721c24;
            opacity: 0.65;
          }
        `}
      </style>

      <ul className="nav nav-tabs" id="myTab" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className="nav-link active"
            id="device-policies-tab"
            data-bs-toggle="tab"
            data-bs-target="#device-policies"
            type="button"
            role="tab"
            aria-controls="device-policies"
            aria-selected="true"
          >
            Device Policies: {totalRows}
          </button>
        </li>
      </ul>

      <div className="d-flex justify-content-between custom_table_action">
        <input
          className="form-control w-25"
          type="text"
          placeholder="Search by Name"
          value={searchTerm}
          onChange={handleSearchInputChange}
        />
        <button
          className="btn primary_btn"
          onClick={() => setShowAddDeviceModal(true)}
          data-bs-toggle="tooltip"
          data-bs-placement="left"
          title="Add New Device Policy"
        >
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </div>
      <div className="h-100vh-150px">
        {loading ? (
          <div className="spin-loader">
            <Loader />
          </div>
        ) : (
          <div className="custom_table">
            <DataTable
              columns={columns}
              data={devicePolicyData}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={countPerPage}
              paginationDefaultPage={page + 1}
              onChangeRowsPerPage={handlePerRowsChange}
              onChangePage={(newPage) => {
                console.log("Changing page:", { newPage, currentPage: page, expectedPage: expectedPageRef.current });
                if (newPage - 1 !== expectedPageRef.current) {
                  console.warn("Unexpected page reset detected:", { newPage, expectedPage: expectedPageRef.current });
                }
                setPage(newPage - 1);
                expectedPageRef.current = newPage - 1;
              }}
              onSort={handleSort}
              sortServer
              highlightOnHover
              fixedHeader
              fixedHeaderScrollHeight="440px"
              paginationComponentOptions={{
                noRowsPerPage: false,
              }}
            />
          </div>
        )}
      </div>
      {isDeleteModalOpen && (
        <div
          className="modal fade discard_modal show"
          id="delete-model"
          style={{ display: "block" }}
          aria-hidden={!isDeleteModalOpen}
          aria-labelledby="exampleModalToggleLabel"
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <p className="modal-title" id="staticBackdropLabel">
                  Delete Device Policy
                </p>
              </div>
              <div className="modal-body">
                <p className="modal-msg-title">Delete</p>
                <p className="modal-msg-desc">
                  Are you sure you want to delete this device policy?
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn_red btn-loader"
                  onClick={confirmDelete}
                  disabled={isDeleteDisabled}
                >
                  {isDeleteDisabled ? "Deleting..." : "Delete"}
                </button>
                <button
                  className="btn btn_light__red mx-2"
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={isDeleteDisabled}
                >
                  Not Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showUpdateDeviceModal && (
        <UpdateDevicePolicy
          show={showUpdateDeviceModal}
          onClose={handleCloseUpdateModal}
          onSubmit={confirmEdit}
          defaultValues={selectedPolicy}
        />
      )}
      {showAddDeviceModal && (
        <AddDevicePolicy
          show={showAddDeviceModal}
          onClose={handleCloseAddModal}
          onSuccess={handleAddSuccess}
        />
      )}
    </div>
  );
}

export default DevicePolicy;